import React from 'react';
import { cn } from '@/lib/cn';
import { Selection, BottomSheet } from '@/components/ui';
import { useStore } from '@/providers/StoreProvider';
import { FilterActionBar } from './FilterActionBar';
import { useBedroomFilter } from './hooks/useBedroomFilter';

export interface BedroomFilterProps {
  variant?: 'desktop' | 'mobile';
  className?: string;
  requireConfirmation?: boolean;
  showLabel?: boolean;
  // Mobile-specific props
  open?: boolean;
  onClose?: () => void;
  page?: 'listings' | 'home';
}

export function BedroomFilter({
  variant = 'desktop',
  showLabel = true,
  className,
  requireConfirmation = false,
  open,
  onClose,
  page = 'listings',
}: BedroomFilterProps) {
  const { bedrooms, toggleBedroom, bedroomValues, labelFor } = useBedroomFilter();
  const resetBedrooms = useStore(s => s.listingsFilters.resetBedrooms);

  const bedroomSelectionContent = (
    <div className={cn('flex w-full items-start gap-2')} role="group" aria-label="Bedrooms">
      {bedroomValues.map(val => {
        const selected = bedrooms.includes(val);
        return (
          <Selection
            key={val}
            selected={selected}
            onClick={() => toggleBedroom(val)}
            className={cn(
              // Layout and sizing
              'grow shrink-0 md:min-w-15',
              // Text alignment
              'text-center',
            )}
          >
            {labelFor(val)}
          </Selection>
        );
      })}
    </div>
  );

  if (variant === 'mobile') {
    return (
      <BottomSheet
        label="Select bedrooms"
        bodyClassName="py-8 px-6"
        open={open!}
        onClose={onClose!}
        leading={false}
        footer={
          requireConfirmation ? (
            <FilterActionBar
              scope="beds"
              onApply={() => {
                onClose?.();
              }}
              {...(page === 'home' ? { primaryButtonText: 'Done' } : {})}
              onReset={() => {
                resetBedrooms();
              }}
            />
          ) : null
        }
      >
        <div className="flex flex-col gap-1">
          {showLabel && (
            <span className={cn('text-body-2-high text-secondary mb-3')}>Bedrooms</span>
          )}
          {bedroomSelectionContent}
        </div>
      </BottomSheet>
    );
  }

  return (
    <div className={cn('flex flex-col', className)}>
      {showLabel && <span className={cn('text-body-2-high text-secondary pb-1')}>Bedrooms</span>}
      {bedroomSelectionContent}
      {requireConfirmation && (
        <FilterActionBar
          shouldUpdateUrl={page === 'listings'}
          scope="beds"
          className="mt-4 !px-0 !py-0 !pt-4"
          onApply={() => {
            onClose?.();
          }}
          {...(page === 'home' ? { primaryButtonText: 'Done' } : {})}
          onReset={() => {
            resetBedrooms();
          }}
        />
      )}
    </div>
  );
}

export default BedroomFilter;
